import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import dayjs from 'dayjs';
import { formatAmountText } from '../../../utils/numberFormat';
import { labelOfDebtType } from '../../../constants/debtEnums';
import { loadState, saveState } from './state';

export const usePlaceholderBinding = ({
  selectedDebt,
  showCreditorPersonal,
  showCreditorEnterprise,
  showDebtorPersonal,
  showDebtorEnterprise,
  editorActionRef,
  setPreviewOpen,
  setPreviewIndex,
}) => {
  const [placeholders, setPlaceholders] = useState([]);
  const [placeholderFieldMap, setPlaceholderFieldMap] = useState(loadState().placeholderFieldMap || {});
  const [placeholderManualMap, setPlaceholderManualMap] = useState(loadState().placeholderManualMap || {});
  const placeholderBindingRef = useRef({ sig: '', map: {} });

  useEffect(() => {
    const phList = Array.isArray(placeholders) ? placeholders : [];
    const nextField = { ...(placeholderFieldMap || {}) };
    const nextManual = { ...(placeholderManualMap || {}) };
    Object.keys(nextField).forEach((k) => { if (!phList.includes(k)) delete nextField[k]; });
    Object.keys(nextManual).forEach((k) => { if (!phList.includes(k)) delete nextManual[k]; });
    phList.forEach((k) => {
      if (!(k in nextField)) nextField[k] = '';
      if (!(k in nextManual)) nextManual[k] = '';
    });
    setPlaceholderFieldMap(nextField);
    setPlaceholderManualMap(nextManual);
    saveState({ placeholderFieldMap: nextField, placeholderManualMap: nextManual });
  }, [placeholders]);

  const fieldOptions = useMemo(() => {
    const groups = [];
    const pushGroup = (label, options) => {
      const list = (options || []).filter(Boolean);
      if (list.length) groups.push({ label, options: list });
    };

    pushGroup('基础', [
      { value: 'today', label: '今日/填发日期' },
    ]);

    pushGroup('债权', [
      { value: 'debt.debtNo', label: '债权编号' },
      { value: 'debt.title', label: '债权名称' },
      { value: 'debt.debtType', label: '债权类型' },
      { value: 'debt.debtOccurrenceDate', label: '债权发生日' },
      { value: 'debt.principalAmount', label: '本金金额' },
      { value: 'debt.totalAmount', label: '债权合计金额' },
    ]);

    if (showCreditorPersonal && !showCreditorEnterprise) {
      pushGroup('债权人（个人）', [
        { value: 'creditor.name', label: '债权人姓名' },
        { value: 'creditor.idNo', label: '债权人身份证号/证件号' },
        { value: 'creditor.phone', label: '债权人联系电话' },
        { value: 'creditor.email', label: '债权人邮箱' },
        { value: 'creditor.province', label: '债权人省' },
        { value: 'creditor.city', label: '债权人市' },
        { value: 'creditor.address', label: '债权人地址' },
      ]);
    } else if (showCreditorEnterprise && !showCreditorPersonal) {
      pushGroup('债权人（企业）', [
        { value: 'creditor.name', label: '债权人企业名称' },
        { value: 'creditor.idNo', label: '债权人统一社会信用代码' },
        { value: 'creditor.companyName', label: '债权人企业名称（字段）' },
        { value: 'creditor.socialCreditCode', label: '债权人统一社会信用代码（字段）' },
        { value: 'creditor.legalPersonName', label: '债权人法定代表人姓名' },
        { value: 'creditor.legalPersonIdNo', label: '债权人法定代表人证件号' },
        { value: 'creditor.phone', label: '债权人联系电话' },
        { value: 'creditor.email', label: '债权人邮箱' },
        { value: 'creditor.province', label: '债权人省' },
        { value: 'creditor.city', label: '债权人市' },
        { value: 'creditor.address', label: '债权人地址' },
      ]);
    } else {
      pushGroup('债权人', [
        { value: 'creditor.name', label: '债权人姓名/企业名称' },
        { value: 'creditor.idNo', label: '债权人身份证号/统一社会信用代码' },
        { value: 'creditor.phone', label: '债权人联系电话' },
        { value: 'creditor.email', label: '债权人邮箱' },
        { value: 'creditor.province', label: '债权人省' },
        { value: 'creditor.city', label: '债权人市' },
        { value: 'creditor.address', label: '债权人地址' },
        { value: 'creditor.companyName', label: '债权人企业名称（字段）' },
        { value: 'creditor.socialCreditCode', label: '债权人统一社会信用代码（字段）' },
        { value: 'creditor.legalPersonName', label: '债权人法定代表人姓名' },
        { value: 'creditor.legalPersonIdNo', label: '债权人法定代表人证件号' },
      ]);
    }

    if (showDebtorPersonal && !showDebtorEnterprise) {
      pushGroup('债务人（个人）', [
        { value: 'debtor.name', label: '债务人姓名' },
        { value: 'debtor.idNo', label: '债务人身份证号/证件号' },
        { value: 'debtor.phone', label: '债务人联系电话' },
        { value: 'debtor.email', label: '债务人邮箱' },
      ]);
    } else if (showDebtorEnterprise && !showDebtorPersonal) {
      pushGroup('债务人（企业）', [
        { value: 'debtor.name', label: '债务人企业名称' },
        { value: 'debtor.idNo', label: '债务人统一社会信用代码' },
        { value: 'debtor.companyName', label: '债务人企业名称（字段）' },
        { value: 'debtor.socialCreditCode', label: '债务人统一社会信用代码（字段）' },
        { value: 'debtor.legalPersonName', label: '债务人法定代表人姓名' },
        { value: 'debtor.legalPersonIdNo', label: '债务人法定代表人证件号' },
        { value: 'debtor.phone', label: '债务人联系电话' },
        { value: 'debtor.email', label: '债务人邮箱' },
      ]);
    } else {
      pushGroup('债务人', [
        { value: 'debtor.name', label: '债务人姓名/企业名称' },
        { value: 'debtor.idNo', label: '债务人身份证号/统一社会信用代码' },
        { value: 'debtor.phone', label: '债务人联系电话' },
        { value: 'debtor.email', label: '债务人邮箱' },
      ]);

      if (showDebtorEnterprise) {
        pushGroup('债务人（企业附加）', [
          { value: 'debtor.companyName', label: '债务人企业名称（字段）' },
          { value: 'debtor.socialCreditCode', label: '债务人统一社会信用代码（字段）' },
          { value: 'debtor.legalPersonName', label: '债务人法定代表人姓名' },
          { value: 'debtor.legalPersonIdNo', label: '债务人法定代表人证件号' },
        ]);
      }
    }

    return groups;
  }, [showCreditorEnterprise, showCreditorPersonal, showDebtorEnterprise, showDebtorPersonal]);

  const fieldKeysSet = useMemo(() => {
    const keys = [];
    const walk = (opts) => {
      (opts || []).forEach((o) => {
        if (!o) return;
        if (Array.isArray(o.options)) {
          walk(o.options);
          return;
        }
        const v = String(o.value || '').trim();
        if (v) keys.push(v);
      });
    };
    walk(fieldOptions);
    return new Set(keys);
  }, [fieldOptions]);

  useEffect(() => {
    setPlaceholderFieldMap((prevMap) => {
      const next = { ...(prevMap || {}) };
      let changed = false;
      Object.keys(next).forEach((ph) => {
        const v = String(next[ph] || '').trim();
        if (!v) return;
        if (!fieldKeysSet.has(v)) {
          next[ph] = '';
          changed = true;
        }
      });
      if (!changed) return prevMap;
      saveState({ placeholderFieldMap: next });
      return next;
    });
  }, [fieldKeysSet]);

  const normalizeKey = (k) => String(k || '').replace(/\*/g, '').trim();
  const liteKey = (k) => normalizeKey(k)
    .replace(/\s+/g, '')
    .replace(/[：:，,。/\\()（）【】[\]{}<>《》\-_—]/g, '');

  const detectCanonicalKey = (rawKey) => {
    const cleaned = normalizeKey(rawKey);
    if (fieldKeysSet.has(cleaned)) return cleaned;

    const k = liteKey(rawKey);
    const has = (s) => k.includes(s);
    const isCreditor = has('债权人');
    const isDebtor = has('债务人');

    if (has('债权发生日') || (has('发生日') && has('债权'))) return 'debt.debtOccurrenceDate';
    if (has('今日') || has('填发日期') || (has('日期') && !has('发生日') && !has('发生日期') && !has('债权发生日'))) return 'today';
    if (has('债权合计金额') || has('合计金额') || (has('合计') && has('金额')) || has('总金额') || has('总额')) return 'debt.totalAmount';
    if (has('本金金额') || (has('本金') && has('金额'))) return 'debt.principalAmount';
    if (has('债权合同名称')) return 'debt.title';
    if (!isCreditor && !isDebtor && has('合同名称')) return 'debt.title';
    if (has('债权名称') || has('债权名') || has('债权标题')) return 'debt.title';
    if (has('债权编号') || (has('债权') && has('编号')) || (has('编号') && has('债权'))) return 'debt.debtNo';
    if (has('债权类型')) return 'debt.debtType';

    if (isCreditor) {
      if (has('统一社会信用代码') || has('信用代码')) return 'creditor.socialCreditCode';
      if (has('企业名称')) return 'creditor.companyName';
      if (has('法定代表人姓名') || has('法人姓名')) return 'creditor.legalPersonName';
      if (has('法定代表人身份证') || has('法定代表人证件') || has('法定代表人证号') || has('法人身份证') || has('法人证件') || has('法人证号')) return 'creditor.legalPersonIdNo';
      if (has('身份证') || has('证件') || has('证号')) return 'creditor.idNo';
      if (has('姓名') || has('名称')) return 'creditor.name';
      if (has('联系电话') || (has('联系') && has('电话')) || has('电话') || has('手机')) return 'creditor.phone';
      if (has('邮箱') || has('电子邮箱') || has('邮件')) return 'creditor.email';
      if (has('省')) return 'creditor.province';
      if (has('市')) return 'creditor.city';
      if (has('地址') || has('详细地址')) return 'creditor.address';
    }

    if (isDebtor || has('个人债务人') || has('企业债务人')) {
      if (has('统一社会信用代码') || has('信用代码')) return 'debtor.socialCreditCode';
      if (has('企业名称')) return 'debtor.companyName';
      if (has('法定代表人姓名') || has('法人姓名')) return 'debtor.legalPersonName';
      if (has('法定代表人身份证') || has('法定代表人证件') || has('法定代表人证号') || has('法人身份证') || has('法人证件') || has('法人证号')) return 'debtor.legalPersonIdNo';
      if (has('身份证') || has('证件') || has('证号')) return 'debtor.idNo';
      if (has('姓名') || has('名称')) return 'debtor.name';
      if (has('联系电话') || (has('联系') && has('电话')) || has('电话') || has('手机')) return 'debtor.phone';
      if (has('邮箱') || has('电子邮箱') || has('邮件')) return 'debtor.email';
    }

    return null;
  };

  const handleSmartMatchFields = () => {
    if (!Array.isArray(placeholders) || placeholders.length === 0) {
      message.warning('模板尚未抽取到占位符');
      return;
    }
    const sig = placeholders.map(p => normalizeKey(p)).join('|');
    if (placeholderBindingRef.current.sig !== sig) {
      const map = {};
      placeholders.forEach((rawKey) => {
        const canonical = detectCanonicalKey(rawKey);
        if (canonical) map[rawKey] = canonical;
      });
      placeholderBindingRef.current = { sig, map };
    }
    const autoMap = placeholderBindingRef.current.map || {};
    const next = { ...(placeholderFieldMap || {}) };
    let applied = 0;
    placeholders.forEach((ph) => {
      const cur = String(next[ph] || '').trim();
      if (cur) return;
      const fk = autoMap[ph];
      if (!fk) return;
      if (!fieldKeysSet.has(fk)) return;
      next[ph] = fk;
      applied += 1;
    });
    setPlaceholderFieldMap(next);
    saveState({ placeholderFieldMap: next });
    message.success(`智能匹配完成（已匹配${applied}项）`);
  };

  const handleReloadTemplate = () => {
    setPreviewOpen?.(false);
    setPreviewIndex?.(0);
    const phList = Array.isArray(placeholders) ? placeholders : [];
    const nextFieldMap = {};
    const nextManualMap = {};
    phList.forEach((ph) => {
      nextFieldMap[ph] = '';
      nextManualMap[ph] = '';
    });
    setPlaceholderFieldMap(nextFieldMap);
    setPlaceholderManualMap(nextManualMap);
    placeholderBindingRef.current = { sig: '', map: {} };
    saveState({ placeholderFieldMap: nextFieldMap, placeholderManualMap: nextManualMap, saved: false });
    editorActionRef?.current?.reload?.();
  };

  const setPlaceholderFieldFor = useCallback((placeholderKey, fieldKey) => {
    setPlaceholderFieldMap((prevMap) => {
      const nextMap = { ...(prevMap || {}) };
      nextMap[placeholderKey] = fieldKey || '';
      saveState({ placeholderFieldMap: nextMap });
      return nextMap;
    });
  }, []);

  const setPlaceholderManualFor = useCallback((placeholderKey, manualValue) => {
    const nv = manualValue ?? '';
    setPlaceholderManualMap((prevMap) => {
      const nextMap = { ...(prevMap || {}) };
      nextMap[placeholderKey] = nv;
      saveState({ placeholderManualMap: nextMap });
      return nextMap;
    });
  }, []);

  const buildValueMapForDebtor = useCallback((debtorRow) => {
    const debt = selectedDebt?.debt || {};
    const creditor = selectedDebt?.creditor || {};
    const debtor = debtorRow || {};

    const today = dayjs().format('YYYY年MM月DD日');
    const debtOccurrenceDate = debt?.debtOccurrenceDate ? dayjs(debt.debtOccurrenceDate).format('YYYY年MM月DD日') : '';

    const creditorType = String(creditor?.creditorType || '');
    const creditorName = creditorType === 'P' ? (creditor?.name || '') : (creditor?.companyName || '');
    const creditorIdNo = creditorType === 'P' ? (creditor?.idCardNo || '') : (creditor?.socialCreditCode || '');

    const debtorType = String(debtor?.type || '');
    const debtorName = debtorType === 'P' ? (debtor?.name || '') : (debtor?.companyName || '');
    const debtorIdNo = debtorType === 'P' ? (debtor?.idNo || '') : (debtor?.socialCreditCode || '');

    return {
      'today': today,
      'debt.debtNo': debt?.debtNo || '',
      'debt.title': debt?.title || '',
      'debt.debtType': labelOfDebtType(debt?.debtType),
      'debt.debtOccurrenceDate': debtOccurrenceDate,
      'debt.principalAmount': formatAmountText(debt?.principalAmount),
      'debt.totalAmount': formatAmountText(debt?.totalAmount),

      'creditor.name': creditorName,
      'creditor.idNo': creditorIdNo,
      'creditor.phone': creditor?.phone || '',
      'creditor.email': creditor?.email || '',
      'creditor.companyName': creditor?.companyName || '',
      'creditor.socialCreditCode': creditor?.socialCreditCode || '',
      'creditor.legalPersonName': creditor?.legalPersonName || '',
      'creditor.legalPersonIdNo': creditor?.legalPersonIdNo || '',
      'creditor.province': creditor?.province || '',
      'creditor.city': creditor?.city || '',
      'creditor.address': creditor?.address || '',

      'debtor.name': debtorName,
      'debtor.idNo': debtorIdNo,
      'debtor.phone': debtor?.phone || '',
      'debtor.email': debtor?.email || '',
      'debtor.companyName': debtor?.companyName || '',
      'debtor.socialCreditCode': debtor?.socialCreditCode || '',
      'debtor.legalPersonName': debtor?.legalPersonName || '',
      'debtor.legalPersonIdNo': debtor?.legalPersonIdNo || '',
    };
  }, [selectedDebt]);

  const buildPlaceholderValuesForDebtor = useCallback((debtorRow) => {
    const phList = Array.isArray(placeholders) ? placeholders : [];
    const valueMap = buildValueMapForDebtor(debtorRow);
    const out = {};
    phList.forEach((ph) => {
      const manual = String((placeholderManualMap || {})[ph] || '').trim();
      if (manual) {
        out[ph] = manual;
        return;
      }
      const fk = String((placeholderFieldMap || {})[ph] || '').trim();
      if (!fk) {
        out[ph] = '';
        return;
      }
      const v = valueMap[fk];
      out[ph] = v === undefined || v === null ? '' : String(v);
    });
    return out;
  }, [buildValueMapForDebtor, placeholders, placeholderFieldMap, placeholderManualMap]);

  const placeholderRows = useMemo(() => (placeholders || []).map((ph) => ({
    key: ph,
    placeholder: ph,
    fieldKey: (placeholderFieldMap || {})[ph] || '',
    manual: (placeholderManualMap || {})[ph] || '',
  })), [placeholders, placeholderFieldMap, placeholderManualMap]);

  return {
    placeholders,
    setPlaceholders,
    placeholderRows,
    fieldOptions,
    handleSmartMatchFields,
    handleReloadTemplate,
    setPlaceholderFieldFor,
    setPlaceholderManualFor,
    buildPlaceholderValuesForDebtor,
  };
};
